import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type Recording, type RecordingTag } from "@shared/schema";
import { useLocation } from "wouter";
import { Search, Play, Clock, User, Globe, Tag, X, Video, Filter } from "lucide-react";
import { RECORDING_THUMBNAIL } from "@/lib/constants";

type LibraryRecording = Recording & {
  classTitle: string;
  providerName: string;
  providerId: string;
  language: string;
  category: string | null;
};

type LibraryResponse = {
  recordings: LibraryRecording[];
  total: number;
};

type ProviderInfo = {
  providerId: string;
  providerName: string;
  count: number;
};

function formatDuration(seconds: number | null): string {
  if (!seconds) return "--:--";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type ActiveLanguage = { value: string; label: string; flag: string };

export default function RecordingLibrary() {
  const { t } = useTranslation();
  const searchParams = useSearch();
  const params = new URLSearchParams(searchParams);
  const initialLang = params.get("language") || "";
  const initialProvider = params.get("provider") || "";
  const initialTag = params.get("tag") || "";

  const [selectedLanguage, setSelectedLanguage] = useState(initialLang);
  const [selectedProvider, setSelectedProvider] = useState(initialProvider);
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTag ? [initialTag] : []);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 12;

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (selectedProvider) p.set("providerId", selectedProvider);
    if (selectedLanguage) p.set("language", selectedLanguage);
    if (selectedTags.length > 0) p.set("tags", selectedTags.join(","));
    if (searchQuery) p.set("search", searchQuery);
    p.set("limit", String(pageSize));
    p.set("offset", String(page * pageSize));
    return p.toString();
  }, [selectedProvider, selectedLanguage, selectedTags, searchQuery, page]);

  const { data: libraryData, isLoading } = useQuery<LibraryResponse>({
    queryKey: [`/api/recordings/library?${queryParams}`],
  });

  const { data: activeLanguages } = useQuery<ActiveLanguage[]>({
    queryKey: ["/api/languages/active"],
  });

  const { data: providers } = useQuery<ProviderInfo[]>({
    queryKey: ["/api/recordings/providers"],
  });

  const { data: allTags } = useQuery<(RecordingTag & { providerName: string })[]>({
    queryKey: ["/api/recordings/tags"],
  });

  const uniqueTags = useMemo(() => {
    if (!allTags) return [];
    const seen = new Set<string>();
    return allTags.filter((t) => {
      if (seen.has(t.name)) return false;
      seen.add(t.name);
      return true;
    });
  }, [allTags]);

  const recordings = libraryData?.recordings || [];
  const total = libraryData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);
  const hasActiveFilters = selectedLanguage || selectedProvider || selectedTags.length > 0;

  function clearAll() {
    setSelectedLanguage("");
    setSelectedProvider("");
    setSelectedTags([]);
    setSearchQuery("");
    setPage(0);
  }

  function toggleTag(tagName: string) {
    setSelectedTags((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
    );
    setPage(0);
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <div className="glass-page-bg" />
      <div className="mb-6">
        <h1 className="text-2xl font-bold glow-text" data-testid="text-library-title">
          {t("recordings.title")}
        </h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("recordings.subtitle")}
        </p>
      </div>

      <div className="mb-6 glass-panel-sm p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("recordings.search")}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            className="pl-9 glass-input"
            data-testid="input-search-recordings"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />

          <Select value={selectedLanguage || "all"} onValueChange={(v) => { setSelectedLanguage(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[160px] glass-input" data-testid="select-language-filter">
              <Globe className="mr-1 h-3 w-3 text-muted-foreground" />
              <SelectValue placeholder={t("recordings.language")} />
            </SelectTrigger>
            <SelectContent className="glass-select-content">
              <SelectItem value="all">{t("recordings.allLanguages")}</SelectItem>
              {(activeLanguages || []).map((l) => (
                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedProvider || "all"} onValueChange={(v) => { setSelectedProvider(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[180px] glass-input" data-testid="select-provider-filter">
              <User className="mr-1 h-3 w-3 text-muted-foreground" />
              <SelectValue placeholder={t("recordings.instructor")} />
            </SelectTrigger>
            <SelectContent className="glass-select-content">
              <SelectItem value="all">{t("recordings.allProviders")}</SelectItem>
              {(providers || []).map((p) => (
                <SelectItem key={p.providerId} value={p.providerId}>
                  {p.providerName} ({p.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="button-clear-filters">
              <X className="mr-1 h-3 w-3" />
              {t("browse.clear")}
            </Button>
          )}
        </div>

        {uniqueTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-3 w-3 text-muted-foreground" />
            {uniqueTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.name)}
                className={`px-3 py-1 text-sm font-medium rounded-full transition-all ${selectedTags.includes(tag.name) ? "cal-glass-pill cal-glass-pill-active" : "cal-glass-pill text-muted-foreground"}`}
                data-testid={`tag-filter-${tag.name}`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="aspect-video w-full rounded-xl" />
              <div className="mt-2 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : recordings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="empty-state-icon mb-4">
            <Video className="h-14 w-14 text-primary/25" />
          </div>
          <h3 className="text-lg font-semibold text-muted-foreground glow-text-sm" data-testid="text-no-recordings">{t("recordings.noRecordings")}</h3>
          <p className="mt-2 text-sm text-muted-foreground/50 max-w-xs leading-relaxed">
            {hasActiveFilters
              ? t("recordings.adjustFilters")
              : t("recordings.noRecordingsYet")
            }
          </p>
          <div className="divider-glow w-24 my-5" />
          {hasActiveFilters && (
            <Button variant="outline" className="mt-2" onClick={clearAll} data-testid="button-clear-all">
              {t("browse.clear")}
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground" data-testid="text-results-count">
              {t("recordings.recordingsFound", { count: total })}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {recordings.map((rec) => (
              <RecordingCard key={rec.id} recording={rec} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="button-prev-page"
              >
                {t("common.previous")}
              </Button>
              <span className="text-sm text-muted-foreground px-3">
                {t("recordings.pageOf", { current: page + 1, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                data-testid="button-next-page"
              >
                {t("common.next")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RecordingCard({ recording }: { recording: LibraryRecording }) {
  const [, navigate] = useLocation();
  const thumbnailUrl = recording.thumbnailUrl || RECORDING_THUMBNAIL;

  return (
    <div className="group cursor-pointer" data-testid={`card-recording-${recording.id}`} onClick={() => navigate(`/recordings/${recording.id}`)}>
      <div className="relative overflow-hidden rounded-xl glass-card glass-card-hover">
        <img
          src={thumbnailUrl}
          alt={recording.title || recording.classTitle}
          className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <div className="flex items-center justify-center rounded-full p-3 glass-card" style={{ boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)" }}>
            <Play className="h-6 w-6 text-white" />
          </div>
        </div>
        {recording.durationSeconds && (
          <div className="absolute bottom-2 right-2 rounded-md px-1.5 py-0.5 text-xs font-medium text-white flex items-center gap-1 glass-card" style={{ fontSize: "11px" }}>
            <Clock className="h-3 w-3" />
            {formatDuration(recording.durationSeconds)}
          </div>
        )}
      </div>
      <div className="mt-2.5 space-y-1">
        <h3 className="text-sm font-medium line-clamp-1" data-testid={`text-recording-title-${recording.id}`}>
          {recording.title || recording.classTitle}
        </h3>
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <span data-testid={`text-recording-provider-${recording.id}`}>{recording.providerName}</span>
          <span>-</span>
          <span>{recording.language}</span>
        </div>
        {recording.tags && recording.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {recording.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] no-default-hover-elevate">
                {tag}
              </Badge>
            ))}
            {recording.tags.length > 3 && (
              <Badge variant="secondary" className="text-[10px] no-default-hover-elevate">
                +{recording.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
